import { useCallback, useEffect, useRef, useState } from 'react';
import type React from 'react';

import { Image } from '@change/ds-core-ui';
import { cn } from '@change/ds-utils';

import { usePreviewUrls } from '../usePreviewUrls';

/**
 * Index of the item whose centre is nearest the container's centre.
 *
 * getBoundingClientRect on both sides, so both share one coordinate space. Mixing `offsetLeft` with
 * `scrollLeft` skews every distance by the container's own page offset and lands on the wrong item —
 * the bug Starter Dashboard's CardCarousel still has.
 */
function findClosestItemIndex(container: HTMLDivElement, items: ReadonlyArray<HTMLButtonElement | null>): number {
	const containerRect = container.getBoundingClientRect();
	const containerCenter = containerRect.left + containerRect.width / 2;

	let closestIndex = 0;
	let smallestDistance = Infinity;

	items.forEach((item, index) => {
		if (!item) return;
		const rect = item.getBoundingClientRect();
		const distance = Math.abs(containerCenter - (rect.left + rect.width / 2));
		if (distance < smallestDistance) {
			smallestDistance = distance;
			closestIndex = index;
		}
	});

	return closestIndex;
}

/** Decorative — `aria-current` on the items is what conveys selection to assistive tech. */
const PaginationDots: React.FC<{ count: number; activeIndex: number }> = ({ count, activeIndex }) => (
	<div aria-hidden className="flex items-center justify-center gap-2">
		{Array.from({ length: count }, (_, index) => (
			<span
				// eslint-disable-next-line react/no-array-index-key
				key={`share-visual-dot-${index}`}
				data-qa="share-visual-carousel-dot"
				className={cn(
					'h-1.5 shrink-0 rounded-full transition-all duration-200',
					activeIndex === index ? 'bg-typography-strong w-5' : 'bg-fill-weaker w-1.5',
				)}
			/>
		))}
	</div>
);

type SelectionCallbacks = {
	onActiveIndexChange: (index: number) => void;
	onSelectionChange: (index: number, interactionType: 'swipe' | 'tap') => void;
	onInteraction: () => void;
};

/**
 * Owns which asset is selected, and keeps its two consumers on different clocks.
 *
 * `applyIndex` runs on every scroll frame, never debounced — what Continue ships must match what is on
 * screen. `reportSelection` is debounced and deduped, because a swipe crossing three assets is one
 * selection, reported where it comes to rest.
 */
function useCarouselSelection(
	scrollContainerRef: React.RefObject<HTMLDivElement | null>,
	itemRefs: React.RefObject<Array<HTMLButtonElement | null>>,
	{ onActiveIndexChange, onSelectionChange, onInteraction }: SelectionCallbacks,
) {
	const [activeIndex, setActiveIndex] = useState(0);
	const activeIndexRef = useRef(0);
	// -1, not 0: starting at 0 asserts that position 0 has already been reported, which is never true.
	// That swallowed the first tap on the already-current asset - the "confirm my choice" tap - so a
	// supporter who deliberately picked the default left no selection event at all.
	const reportedIndexRef = useRef(-1);
	const scrollTimeoutRef = useRef<number | null>(null);
	/**
	 * Set while a tap's smooth scroll is animating. A tap decides the selection up front, but the
	 * animation's own scroll events would then walk `applyIndex` through every card it passes — so
	 * tapping the third card selected it, reverted to the first, and crawled back over ~220ms. Visible
	 * as a flicker, and a supporter who tapped Continue inside that window shared the wrong asset.
	 */
	const programmaticScrollRef = useRef(false);
	/** Position the pending settle timer would report, kept so unmount can flush instead of drop it. */
	const pendingIndexRef = useRef<number | null>(null);

	const applyIndex = useCallback(
		(index: number) => {
			// eslint-disable-next-line react-hooks/refs -- written from event handlers, never during render.
			if (activeIndexRef.current === index) return;
			// eslint-disable-next-line react-hooks/refs -- same.
			activeIndexRef.current = index;
			setActiveIndex(index);
			onActiveIndexChange(index);
		},
		[onActiveIndexChange],
	);

	const reportSelection = useCallback(
		(index: number, interactionType: 'swipe' | 'tap') => {
			// eslint-disable-next-line react-hooks/refs -- event handlers only.
			if (reportedIndexRef.current === index) return;
			// eslint-disable-next-line react-hooks/refs -- same.
			reportedIndexRef.current = index;
			onSelectionChange(index, interactionType);
		},
		[onSelectionChange],
	);

	useEffect(() => {
		const container = scrollContainerRef.current;
		if (!container) return undefined;

		const handleScroll = () => {
			if (container.clientWidth === 0) return;

			// A tap already decided the selection; the scroll it started must not re-decide it mid-flight.
			// Suppression is limited to those intermediate frames - the settle timer below clears the flag
			// and reconciles against wherever the strip actually stopped.
			// eslint-disable-next-line react-hooks/refs -- event handler only.
			const isProgrammatic = programmaticScrollRef.current;
			const closest = findClosestItemIndex(container, itemRefs.current);
			if (!isProgrammatic) {
				// Reported before the dedup: a drag that snaps back to the same card never changes the index,
				// but the supporter still used the picker.
				onInteraction();
				applyIndex(closest);
			}

			if (scrollTimeoutRef.current) window.clearTimeout(scrollTimeoutRef.current);
			// Cleared rather than recorded while a tap animates: the tap already reported its own choice, so
			// flushing a frame of its animation at teardown would emit a second selection for a card the
			// supporter only scrolled past. Clearing also drops an earlier swipe the tap superseded.
			// eslint-disable-next-line react-hooks/refs -- same.
			pendingIndexRef.current = isProgrammatic ? null : closest;
			scrollTimeoutRef.current = window.setTimeout(() => {
				// eslint-disable-next-line react-hooks/refs -- same.
				programmaticScrollRef.current = false;
				// eslint-disable-next-line react-hooks/refs -- same.
				pendingIndexRef.current = null;
				// Reconciled even out of a suppressed programmatic scroll: a fast flick can interrupt a tap's
				// smooth scroll and stay inside the guard's window from start to finish, so every scroll
				// event it produced was ignored above and the carousel stopped somewhere the selection never
				// followed - Continue then shipped the tapped asset while another one was on screen.
				applyIndex(closest);
				// Deduped, so an uninterrupted tap - already reported as 'tap' by the click - is not reported
				// a second time here.
				reportSelection(closest, 'swipe');
			}, 80);
		};

		/**
		 * A finger on the strip means the supporter is driving. An animation never puts a pointer down, so
		 * this is the one signal separating the two - without it the guard stayed up through an
		 * interrupting flick and Continue shipped the tapped card. A tap cannot cancel its own guard:
		 * pointerdown bubbles from the card and fires before the click that arms it.
		 */
		const handlePointerDown = () => {
			// eslint-disable-next-line react-hooks/refs -- event handler only, never read during render.
			programmaticScrollRef.current = false;
		};

		container.addEventListener('scroll', handleScroll);
		container.addEventListener('pointerdown', handlePointerDown);

		return () => {
			container.removeEventListener('scroll', handleScroll);
			container.removeEventListener('pointerdown', handlePointerDown);
			// eslint-disable-next-line react-hooks/refs -- teardown, never during render.
			if (scrollTimeoutRef.current !== null) window.clearTimeout(scrollTimeoutRef.current);
			// Flushed rather than dropped. Closing the modal mid-swipe tears this down before the settle
			// timer fires, and simply clearing it lost the selection entirely: no asset_selected event, and
			// the cancel event still carrying position 0 / interacted false after a visible swipe. Only the
			// report is flushed - applyIndex would set state on a component that is going away.
			// eslint-disable-next-line react-hooks/refs -- same.
			const pending = pendingIndexRef.current;
			if (pending !== null) reportSelection(pending, 'swipe');
		};
	}, [applyIndex, reportSelection, onInteraction, scrollContainerRef, itemRefs]);

	const beginProgrammaticScroll = useCallback(() => {
		// The previous swipe's settle timer would otherwise fire mid-animation: it reports a stale
		// position, and it clears the guard below, letting the tap's own scroll re-decide the selection
		// the tap just made.
		// eslint-disable-next-line react-hooks/refs -- event handlers only, never read during render.
		if (scrollTimeoutRef.current !== null) window.clearTimeout(scrollTimeoutRef.current);
		// eslint-disable-next-line react-hooks/refs -- same.
		scrollTimeoutRef.current = null;
		// The tap supersedes that swipe outright, so teardown in the gap before this animation emits its
		// first frame must not flush the swipe's position as a second, later selection.
		// eslint-disable-next-line react-hooks/refs -- same.
		pendingIndexRef.current = null;
		// eslint-disable-next-line react-hooks/refs -- same.
		programmaticScrollRef.current = true;
	}, []);

	return { activeIndex, applyIndex, reportSelection, beginProgrammaticScroll };
}

/**
 * Height first, width derived from the 9:16 asset ratio. Kept as CSS variables so the container's
 * centring padding — `(100% - width) / 2` — can be plain CSS instead of a measured value.
 */
const CARD_SIZE_VARS = {
	'--share-card-h': '42svh',
	'--share-card-w': 'calc(42svh * 9 / 16)',
} as React.CSSProperties;

export const ShareVisualCarousel: React.FC<Props> = ({
	previews,
	label,
	itemLabel,
	onActiveIndexChange,
	onSelectionChange,
	onInteraction,
}) => {
	const previewUrls = usePreviewUrls(previews);
	const scrollContainerRef = useRef<HTMLDivElement | null>(null);
	const itemRefs = useRef<Array<HTMLButtonElement | null>>([]);
	const { activeIndex, applyIndex, reportSelection, beginProgrammaticScroll } = useCarouselSelection(
		scrollContainerRef,
		itemRefs,
		{ onActiveIndexChange, onSelectionChange, onInteraction },
	);

	const setItemRef = useCallback(
		(index: number) => (element: HTMLButtonElement | null) => {
			itemRefs.current[index] = element;
		},
		[],
	);

	// Scrolls by the delta between the two centres rather than scrollIntoView, which would also scroll
	// the modal itself to bring the item into view.
	const scrollToItem = useCallback(
		(index: number) => {
			const item = itemRefs.current[index];
			const container = scrollContainerRef.current;
			if (!item || !container) return;

			const containerRect = container.getBoundingClientRect();
			const itemRect = item.getBoundingClientRect();
			const delta = itemRect.left + itemRect.width / 2 - (containerRect.left + containerRect.width / 2);

			// Tapping the already-centred card leaves a sub-pixel delta (measured: 0.008px on device), and
			// a scroll that small emits no scroll event at all - so the settle timer that lowers the guard
			// would never be scheduled and the supporter's next real swipe would be ignored wholesale.
			// Threshold rather than `=== 0` because the resting delta is never exactly zero.
			if (Math.abs(delta) < 1) return;

			beginProgrammaticScroll();
			container.scrollBy({ left: delta, behavior: 'smooth' });
		},
		[beginProgrammaticScroll],
	);

	return (
		<div className="flex flex-col gap-4" style={CARD_SIZE_VARS}>
			{/* Sized from the viewport height, not from the card width. A width-driven card (`w-full` in a
			    padded container) resolves to 16/9 of that container, which on a 375x667 device is ~427px
			    tall and pushes the share CTA below the fold — the supporter has to scroll to find the
			    primary action. Driving height instead keeps the whole modal on one screen, at the cost of a
			    smaller asset on short devices.

			    Both dimensions are expressed in `svh`, so the centring padding can stay pure CSS:
			    `(100% - cardWidth) / 2` is what centres the first and last asset with no dead space. `svh`
			    rather than `vh` so mobile browser chrome is accounted for. */}
			<div
				ref={scrollContainerRef}
				// `role="group"` so the label is announced: assistive tech ignores aria-label on a plain div.
				role="group"
				aria-label={label}
				data-qa="share-visual-carousel"
				data-testid="share-visual-carousel"
				className="flex h-[var(--share-card-h)] snap-x snap-mandatory [scrollbar-width:none] gap-4 overflow-x-auto overscroll-x-contain px-[calc((100%-var(--share-card-w))/2)] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
			>
				{previewUrls.map((src, index) => (
					<button
						type="button"
						// The list is fixed-length and never reordered within a mount, and the object URL is not a
						// usable key — the test environment's createObjectURL stub returns one value for every call.
						// eslint-disable-next-line react/no-array-index-key
						key={`share-visual-preview-${index}`}
						ref={setItemRef(index)}
						aria-current={activeIndex === index}
						// Indexed, so the options are distinguishable: a shared label reads out as the same
						// button N times over.
						aria-label={itemLabel(index)}
						data-qa="share-visual-carousel-item"
						data-testid="share-visual-carousel-item"
						className={cn(
							// will-change promotes each card to its own compositor layer, so the opacity swap is
							// applied by the compositor rather than painted into the scroller's tiles. Without it,
							// iOS defers repaints of tiles behind the scroll direction until the scroll settles, so
							// the outgoing card held full opacity through the swipe and snapped to 0.4 at the end
							// (verified frame-by-frame on device) while the incoming card faded normally.
							'h-full w-[var(--share-card-w)] shrink-0 snap-center transition-opacity duration-200 will-change-[opacity]',
							activeIndex === index ? 'opacity-100' : 'opacity-40',
						)}
						onClick={() => {
							onInteraction();
							applyIndex(index);
							reportSelection(index, 'tap');
							scrollToItem(index);
						}}
					>
						{/* Empty alt: the button already carries the name, and repeating it announces twice. */}
						<Image className="h-full w-full rounded-xl object-cover" aspectRatio="9:16" src={src} alt="" />
					</button>
				))}
			</div>
			<PaginationDots count={previewUrls.length} activeIndex={activeIndex} />
		</div>
	);
};

type Props = {
	previews: readonly File[];
	label: string;
	/** Accessible name for one item, e.g. "Share visual 2 of 4". */
	itemLabel: (index: number) => string;
	/** Fired on every move, immediately — this decides which asset Continue ships. */
	onActiveIndexChange: (selectedIndex: number) => void;
	/**
	 * Fired on every tap, before the selection dedups. Tapping the already-current asset changes
	 * nothing, so it reports neither a move nor a selection — but the supporter still engaged with the
	 * picker, and `share_asset_interacted` exists to tell that apart from ignoring the carousel.
	 */
	onInteraction: () => void;
	/** Fired once the carousel settles — tracking only. */
	onSelectionChange: (selectedIndex: number, interactionType: 'swipe' | 'tap') => void;
};
